import { useState, useEffect, useCallback, useRef } from 'react';
import { getMainContactOptions } from '../services/profileService';
import { useUserStore } from '../../auth/store/userStore';
import { handleApiError } from '../../../shared/services/toast';
import { findCurrentMainContactOption, findContactOptionByName } from '../../../shared/utils/mainContact';

export function useMainContact({ canEditBillingAndContact, setValue, watch }) {
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  
  const { profile, getClientId, getSiteId } = useUserStore();

  // Last profile-supplied contact name applied to the form, so the sync effect below runs once per
  // change rather than re-asserting itself over the user's selection.
  const lastSyncedProfileContactRef = useRef(null);
  const selectedContactId = watch('mainContactId');

  // Get client and site IDs
  const clientId = getClientId();
  const siteId = getSiteId();

  // Format contact options helper
  const formatContactOptions = useCallback((options) => 
    options.filter(option => option.username !== "General User").map(option => ({
      id: option.id,
      value: option.id.toString(),
      label: option.username,
      name: option.username,
      isCurrentMainContact: option.isCurrentMainContact
    }))
  , []);

  // Handle contact change
  const handleContactChange = useCallback((contactId) => {
    if (contactId === '' || contactId === null) {
      setValue('mainContactId', '');
      setValue('mainContactUsername', '');
    } else {
      const selectedOption = contactOptions.find(opt => opt.value === contactId);
      if (selectedOption) {
        setValue('mainContactId', contactId);
        setValue('mainContactUsername', selectedOption.name);
      }
    }
  }, [contactOptions, setValue]);

  // Auto-select contact helper — the backend's current-main-contact flag, else the profile's main
  // contact by name (display name vs username, see shared/utils/mainContact), else the only contact.
  const autoSelectContact = useCallback((options, profileData) => {
    const currentContact = findCurrentMainContactOption(options, { name: profileData?.mainContact?.name });
    if (!currentContact) return false;
    setValue('mainContactId', currentContact.value);
    setValue('mainContactUsername', currentContact.name);
    return true;
  }, [setValue]);

  // Load contact options
  useEffect(() => {
    const loadContactOptions = async () => {
      if (!clientId || !siteId || !canEditBillingAndContact) {
        setContactOptions([]);
        return;
      }
      
      setLoadingContacts(true);
      try {
        const options = await getMainContactOptions(clientId, siteId);
        const formattedOptions = formatContactOptions(options);
        setContactOptions(formattedOptions);
        
        // Auto-select contact on initial load
        autoSelectContact(formattedOptions, profile);
      } catch (error) {
        handleApiError(error, 'Failed to load main contacts');
        setContactOptions([]);
      } finally {
        setLoadingContacts(false);
      }
    };

    loadContactOptions();
  }, [clientId, siteId, canEditBillingAndContact, formatContactOptions, autoSelectContact, profile]);

  // Adopt the main contact from the profile when it changes underneath the form — e.g. after a save,
  // or when someone edits it from the billing dialog.
  //
  // Applied once per distinct profile value, tracked by the ref. It used to depend on
  // selectedContactId and compare against it, which meant the user's own pick counted as being out
  // of sync: choose someone new, the effect re-fired and immediately put the saved contact back, so
  // the dropdown could not be changed at all. The ref keeps the profile-driven sync while leaving a
  // deliberate selection alone.
  useEffect(() => {
    const profileContactName = profile?.mainContact?.name;

    if (contactOptions.length === 0 || !profileContactName) {
      return;
    }

    if (lastSyncedProfileContactRef.current === profileContactName) {
      return;
    }

    const profileContact = findContactOptionByName(contactOptions, profileContactName);
    if (profileContact) {
      lastSyncedProfileContactRef.current = profileContactName;
      setValue('mainContactId', profileContact.value);
      setValue('mainContactUsername', profileContact.name);
    }
  }, [profile?.mainContact?.name, contactOptions, setValue]);

  return {
    contactOptions,
    loadingContacts,
    selectedContactId,
    handleContactChange,
    clientId,
    siteId,
    canLoadContacts: !!(clientId && siteId && canEditBillingAndContact)
  };
}
